/**
 * Story teasers. Each story is teaser + wall + (real) body strings.
 * The body strings ship in the bundle — they are news copy, not the
 * dispatch — and the wall decides how much of them you see: exactly
 * none past the teaser, as has been the policy since the wall policy.
 */

import { FRONT_PAGE, type Story } from "./data";
import { presentSealed, presentSignup, presentWall, readerState } from "./gate-bridge";

const ART_BY_KIND: Record<Story["art"], string> = {
  globe: "wire-art--globe",
  duck: "wire-art--duck",
  capsule: "wire-art--capsule",
  box: "wire-art--box",
};

function esc(s: string): string {
  return s.replace(/&/g, "&amp;").replace(/</g, "&lt;").replace(/>/g, "&gt;");
}

export function renderStories(host: HTMLElement): void {
  host.innerHTML = FRONT_PAGE.map((story, i) => {
    const art = ART_BY_KIND[story.art];
    const readLabel = readerState() === "signed-up" ? "Continue reading" : "Continue reading";
    return `
    <article class="wire-story" data-story="${esc(story.id)}">
      <div class="wire-story__art ${art}" data-ws-art="${esc(story.art)}" aria-hidden="true"></div>
      <div class="wire-story__main">
        <p class="wire-kicker">${esc(story.kicker)}</p>
        <h2 class="wire-story__headline">${esc(story.headline)}</h2>
        <p class="wire-story__dek">${esc(story.dek)}</p>
        <p class="wire-story__byline">${esc(story.byline)} · ${esc(story.dateline)}</p>
        <p class="wire-story__teaser">${esc(story.teaser)}</p>
        <div class="wire-story__actions">
          <button class="wire-story__read" type="button" data-read="${esc(story.id)}">${readLabel}</button>
          <span class="wire-story__index">${String(i + 1).padStart(2, "0")} / ${String(FRONT_PAGE.length).padStart(2, "0")}</span>
        </div>
        <div class="wire-comments" data-comments-for="${esc(story.id)}"></div>
      </div>
    </article>`;
  }).join("\n");

  for (const btn of Array.from(host.querySelectorAll<HTMLButtonElement>("[data-read]"))) {
    btn.addEventListener("click", () => {
      const story = FRONT_PAGE.find((s) => s.id === btn.dataset.read);
      if (!story) return;
      if (readerState() === "anonymous") presentSignup();
      else presentWall();
    });
  }
}

/** Premium sealed card on the front page. A lock, not a hint. */
export function renderPremiumCard(host: HTMLElement): void {
  host.innerHTML = `
  <section class="wire-premium-card" data-premium>
    <div class="wire-premium-card__stamp" data-ws-seal aria-hidden="true"></div>
    <div>
      <p class="wire-kicker wire-kicker--premium">Premium · Edition 41.2</p>
      <h2 class="wire-premium-card__title">The sealed dispatch</h2>
      <p class="wire-premium-card__note">This month's premium wire went out sealed. Editors only.</p>
    </div>
    <button class="wire-premium-card__cta" type="button" data-seal="1">Inspect seal</button>
  </section>`;
  const cta = host.querySelector<HTMLButtonElement>("[data-seal]");
  cta?.addEventListener("click", () => presentSealed());
}
